import { type JSX, useState } from "react";

import { TemplateEditor } from "common/components/Templates/index";
import TemplatePreviewPlaceholder from "common/components/Templates/TemplatePreviewPlaceholder";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  OverlayDialogContent,
  OverlayDialogTitle,
} from "common/components/ui/Dialog";
import { Drawer, DrawerContent } from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import { TEMPLATE_TYPES } from "common/constants";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import type { CreatedTemplate, Template } from "common/types";

import type { UseFormReturn } from "react-hook-form";

interface MobileTemplateProps {
  saveButton: JSX.Element;
  onClose: () => void;
  previewComponent: JSX.Element;
  updatePreview: () => void;
  form: UseFormReturn<Template | CreatedTemplate>;
}

// this is used for both editing and creating new templates on mobile, since the experience is identical
export default function MobileTemplate({
  saveButton,
  onClose,
  previewComponent,
  updatePreview,
  form,
}: MobileTemplateProps) {
  const { accounts_count: accountsCount } = useSelectedWorkspaceContext();
  const [isShowingPreview, setIsShowingPreview] = useState(false);

  const { type, ...templateRest } = form.getValues();

  function showPreview() {
    updatePreview();
    setIsShowingPreview(true);
  }

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <OverlayDialogContent>
          <OverlayDialogTitle>
            {"id" in templateRest ? "Edit" : "Create"}{" "}
            {TEMPLATE_TYPES[type].label.toLowerCase()}
          </OverlayDialogTitle>
          <div className="flex flex-col px-4 py-6 flex-1">
            <FormField
              label="Template name"
              htmlFor="templateName"
              className="mb-4"
              size="lg"
              error={form.formState.errors.name?.message}
            >
              <Input
                {...form.register("name")}
                hasError={!!form.formState.errors.name?.message}
                variant="lg"
              />
            </FormField>
            <TemplateEditor form={form} isEditable />
          </div>
          <div className="p-4 flex justify-between">
            <DialogClose asChild>
              <Button variant="secondary-black" size="lg">
                {"id" in templateRest ? "Cancel" : "Discard"}
              </Button>
            </DialogClose>
            <Button variant="default-black" size="lg" onClick={showPreview}>
              Preview
            </Button>
          </div>
        </OverlayDialogContent>
      </Dialog>
      <Drawer open={isShowingPreview} onOpenChange={setIsShowingPreview}>
        <DrawerContent>
          {accountsCount > 0 ? (
            previewComponent
          ) : (
            <TemplatePreviewPlaceholder />
          )}
          <div className="mt-10 flex justify-between">
            <Button
              variant="secondary-black"
              size="lg"
              onClick={() => setIsShowingPreview(false)}
            >
              Back
            </Button>
            {saveButton}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
